import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { AdminAPI, extractError } from "@/lib/api";
import { useFormatters } from "@/lib/format";

const ROLES = [
    { value: "any",             label: "All roles" },
    { value: "owner",           label: "Owner" },
    { value: "service_advisor", label: "Service advisor" },
    { value: "technician",      label: "Technician" },
    { value: "parts_manager",   label: "Parts manager" },
    { value: "accountant",      label: "Accountant" },
    { value: "platform_admin",  label: "Platform admin" },
];

const ACTIVE = [
    { value: "any",   label: "All" },
    { value: "true",  label: "Active" },
    { value: "false", label: "Inactive" },
];

export default function AdminUsersPage() {
    const { formatDate } = useFormatters();
    const [tenants, setTenants] = useState([]);
    const [filters, setFilters] = useState({ tenant_id: "any", role: "any", is_active: "any" });
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        AdminAPI.listTenants({ page_size: 200 })
            .then((r) => setTenants(r.items || []))
            .catch(() => setTenants([]));
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        const params = { page, page_size: 50 };
        if (filters.tenant_id !== "any") params.tenant_id = filters.tenant_id;
        if (filters.role !== "any") params.role = filters.role;
        if (filters.is_active !== "any") params.is_active = filters.is_active === "true";
        try {
            const r = await AdminAPI.listUsers(params);
            setItems(r.items || []);
            setTotal(r.total || 0);
        } catch (err) {
            toast.error(extractError(err));
        } finally {
            setLoading(false);
        }
    }, [filters, page]);

    useEffect(() => { load(); }, [load]);

    const tenantName = (tid) => tenants.find((t) => t.id === tid)?.name || tid || "—";
    const pageCount = Math.max(1, Math.ceil(total / 50));

    return (
        <div className="space-y-6" data-testid="admin-users-page">
            <header>
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                    Platform · Users
                </div>
                <h1 className="mt-1 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                    Cross-tenant users
                </h1>
                <p className="mt-1 text-sm text-zinc-500">
                    Read-only view across every workshop. CRUD lives on the per-tenant detail page.
                </p>
            </header>

            <Card className="grid grid-cols-1 gap-3 p-4 md:grid-cols-4" data-testid="users-filters">
                <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Tenant</Label>
                    <Select
                        value={filters.tenant_id}
                        onValueChange={(v) => { setPage(1); setFilters((f) => ({ ...f, tenant_id: v })); }}
                    >
                        <SelectTrigger data-testid="filter-tenant"><SelectValue placeholder="All tenants" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="any">All tenants</SelectItem>
                            {tenants.map((t) => (
                                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Role</Label>
                    <Select
                        value={filters.role}
                        onValueChange={(v) => { setPage(1); setFilters((f) => ({ ...f, role: v })); }}
                    >
                        <SelectTrigger data-testid="filter-role"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Active</Label>
                    <Select
                        value={filters.is_active}
                        onValueChange={(v) => { setPage(1); setFilters((f) => ({ ...f, is_active: v })); }}
                    >
                        <SelectTrigger data-testid="filter-active"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {ACTIVE.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex items-end">
                    <span className="text-xs text-zinc-500" data-testid="users-total">
                        {loading ? "…" : `${total} users`}
                    </span>
                </div>
            </Card>

            <Card className="overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Email</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Tenant</TableHead>
                            <TableHead>Role</TableHead>
                            <TableHead>Active</TableHead>
                            <TableHead>Created</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-zinc-500">Loading…</TableCell></TableRow>
                        ) : items.length === 0 ? (
                            <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-zinc-500">No users.</TableCell></TableRow>
                        ) : (
                            items.map((u) => (
                                <TableRow key={u.id} data-testid={`xt-user-row-${u.email}`}>
                                    <TableCell className="font-mono text-xs">{u.email}</TableCell>
                                    <TableCell>{u.name || "—"}</TableCell>
                                    <TableCell className="text-sm text-zinc-600 dark:text-zinc-400">
                                        {tenantName(u.tenant_id)}
                                    </TableCell>
                                    <TableCell>
                                        <Badge className="border-zinc-300 bg-zinc-100 text-zinc-700 hover:bg-zinc-100">
                                            {u.role || "—"}
                                        </Badge>
                                        {u.is_platform_admin && (
                                            <Badge className="ml-1 border-red-300 bg-red-100 text-red-700 hover:bg-red-100">
                                                platform
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {u.is_active ? (
                                            <span className="font-mono text-xs text-emerald-700">yes</span>
                                        ) : (
                                            <span className="font-mono text-xs text-zinc-400">no</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="font-mono text-xs text-zinc-500">
                                        {u.created_at ? formatDate(u.created_at) : "—"}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Card>

            {pageCount > 1 && (
                <div className="flex items-center justify-between text-sm text-zinc-500">
                    <span>Page {page} of {pageCount}</span>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={page <= 1}
                                 onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</Button>
                        <Button variant="outline" size="sm" disabled={page >= pageCount}
                                 onClick={() => setPage((p) => p + 1)}>Next</Button>
                    </div>
                </div>
            )}
        </div>
    );
}
